import type { FC } from 'react';
import cn from 'classnames';

import type { WithTestId } from 'types/app';

import { ImageFallback } from '@Atomic/molecules/ImageFallback';
import { Image } from '@Atomic/atoms/Image';

import styles from './style.module.scss';
import type { OfferCarouselProps } from './types';
import { getInitialSlide } from './utils';

export const OfferCarouselPlaceholder: FC<WithTestId<OfferCarouselProps>> = ({
  images,
  primaryId,
  isExpired,
  testId,
}) => {
  const moreThanOne = images.length > 1;
  // The photo the carousel opens on after hydration, so the swap doesn't change the picture
  const initialSlide = getInitialSlide(images, primaryId);
  const initialImage = images[initialSlide];

  return (
    <div
      className={cn(styles.wrapper, styles.placeholder, { [styles.expired]: isExpired })}
      data-testid={testId}
    >
      <div className={styles.mainGalleryWrapper}>
        <div className={cn(styles.mainGallery, { [styles.moreThanOne]: moreThanOne })}>
          {initialImage && (
            <div className={styles.gallerySlide}>
              <Image
                priority
                blurredPad
                className={styles.galleryImage}
                fallback={<ImageFallback type="full" />}
                objectFit="contain"
                alt={initialImage.alt}
                title={initialImage.alt}
                src={initialImage.image}
              />
            </div>
          )}
        </div>

        {moreThanOne && (
          <div className={styles.bullets}>
            {images.map((_, index) => (
              <div
                key={index}
                className={cn(styles.bullet, { [styles.bulletActive]: index === initialSlide })}
              />
            ))}
          </div>
        )}
      </div>

      <div className={cn(styles.thumbsGalleryWrapper, { [styles.moreThanOne]: moreThanOne })}>
        <div className={styles.thumbsGallery}>
          {images.map(({ alt, image, id }) => (
            <div key={id} className={styles.thumbsSlide}>
              <Image
                className={styles.thumbImage}
                fallback={<ImageFallback type="thumbnail" />}
                title={alt}
                src={image}
                alt={alt}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
